import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function formatDateDisplay(date: Date | undefined): string {
  if (!date) return "";
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

function parseDateString(value: string): Date | undefined {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;
  const [, day, month, year] = match;
  const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (
    isNaN(d.getTime()) ||
    d.getDate() !== parseInt(day) ||
    d.getMonth() + 1 !== parseInt(month) ||
    d.getFullYear() !== parseInt(year)
  )
    return undefined;
  return d;
}

interface DatePickerProps {
  id?: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  required?: boolean;
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = "ΗΗ/ΜΜ/ΕΕΕΕ",
  required,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(formatDateDisplay(value));

  // Sync input when value changes externally
  React.useEffect(() => {
    setInputValue(formatDateDisplay(value));
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^\d/]/g, "");
    const digits = val.replace(/\//g, "");
    if (digits.length >= 4) {
      val = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    } else if (digits.length >= 2) {
      val = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    if (val.length <= 10) {
      setInputValue(val);
      const parsed = parseDateString(val);
      if (parsed) {
        onChange(parsed);
      }
    }
  };

  const handleInputBlur = () => {
    if (inputValue && !parseDateString(inputValue)) {
      setInputValue(formatDateDisplay(value));
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    onChange(date);
    setInputValue(formatDateDisplay(date));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          maxLength={10}
          required={required}
          className="pr-10"
        />
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={cn(
              "absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            )}
          >
            <CalendarIcon className="size-4" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleCalendarSelect}
          defaultMonth={value}
        />
      </PopoverContent>
    </Popover>
  );
}
